import React, {useEffect, useState} from "react";
import {useNavigate, useParams} from "react-router-dom";
import {useNotification} from "../../utils/hooks/useNotification";
import {useForm} from "react-hook-form";
import {zodResolver} from "@hookform/resolvers/zod";
import {deleteFetcher, getFetcher, putFetcher} from "../../utils/api/fetchers";
import {Category, categorySchema} from "../../schemas/category/categoriesSchema";
import {Box, Button, Paper, Stack, TextField, Typography} from "@mui/material";
import useSWR from "swr";
import Loading from "../../components/status/Loading";
import ErrorMessage from "../../components/status/ErrorMessage";
import {useAuth} from "../../utils/hooks/useAuth";
import {useConfirm} from "material-ui-confirm";
import UserRole from "../../schemas/user/roles";
import CustomRichTextEditor from "../../components/input/CustomRichTextEditor";

const CategoryDetails: React.FC = () => {
    const {hasPermission} = useAuth();
    const confirm = useConfirm();
    const navigate = useNavigate();
    const {categoryId} = useParams<{ categoryId: string }>();
    const {showNotification} = useNotification();
    const {data: category, isLoading, error, mutate} = useSWR<Category>(`/categories/${categoryId}`, getFetcher);
    const {
        register,
        handleSubmit,
        setValue,
        reset,
        formState: {errors, isDirty},
        watch
    } = useForm<Category>({
        defaultValues: category,
        resolver: zodResolver(categorySchema),
    });

    const [editMode, setEditMode] = useState<boolean>(false);
    const readOnly = !hasPermission(UserRole.COMMODORE);
    const noEdit = readOnly || !editMode;

    useEffect(() => {
        if (category) {
            reset(category);
        }
    }, [category, reset]);

    const onSubmit = async (data: Category) => {
        if (!isDirty) {
            showNotification("Nebyly provedeny žádné změny", "info");
            setEditMode(false);
            return;
        }

        putFetcher(`/categories/${categoryId}`, data)
            .then(() => {
                showNotification("Kategorie byla úspěšně upravena", "success");
                mutate();
                setEditMode(false);
            })
            .catch((error) => {
                if (error.response?.status === 409) {
                    showNotification("Kategorie s tímto jménem nebo číslem již existuje", "error");
                } else {
                    showNotification("Nepodařilo se upravit kategorii", "error");
                }
            });
    };

    const handleDelete = () => {
        confirm({
            description: `Opravdu chcete smazat tuto kategorii?`,
            title: "Smazat kategorii",
            confirmationText: "Smazat",
            cancellationText: "Zrušit",
            confirmationButtonProps: {variant: "contained", color: "error"},
        })
            .then(() => {
                deleteFetcher(`/categories/${categoryId}`).then(() => {
                    showNotification('Kategorie byla smazána', 'success');
                    navigate('/categories');
                }).catch(() => {
                    showNotification('Kategorii se nepodařilo smazat', 'error');
                });
            })
            .catch(() => {
            });
    };

    if (isLoading || !category) return <Loading/>;
    if (error) return <ErrorMessage/>

    return (
        <Paper elevation={3} sx={{padding: 4, maxWidth: 600, margin: '0 auto'}}>
            <Typography variant="h4" gutterBottom>
                Detail kategorie
            </Typography>
            <Box component="form" noValidate onSubmit={handleSubmit(onSubmit)}>
                <Stack spacing={3}>
                    <TextField
                        autoFocus
                        fullWidth
                        label="Jméno"
                        variant="standard"
                        required
                        {...register('name')}
                        error={!!errors.name}
                        helperText={errors.name?.message}
                        InputProps={{readOnly: noEdit}}
                    />
                    <TextField
                        fullWidth
                        label="Číslo kategorie"
                        variant="standard"
                        type="number"
                        required
                        {...register('categoryNumber', {valueAsNumber: true})}
                        error={!!errors.categoryNumber}
                        helperText={errors.categoryNumber?.message}
                        InputProps={{
                            readOnly: noEdit,
                            inputProps: {min: 1}
                        }}
                    />
                    <CustomRichTextEditor
                        title="Popis"
                        value={watch('description') ?? undefined}
                        onChange={(value) => setValue('description', value, {shouldDirty: true})}
                        readOnly={noEdit}
                        error={errors.description?.message}
                    />
                    {!readOnly && (
                        editMode ? (
                            <Stack direction='row' spacing={2}>
                                <Button
                                    variant="contained"
                                    color="error"
                                    sx={{width: 250}}
                                    onClick={() => {
                                        setEditMode(false);
                                        reset(category);
                                    }}
                                >
                                    Zrušit změny
                                </Button>
                                <Button
                                    fullWidth
                                    variant="contained"
                                    onClick={handleSubmit(onSubmit)}
                                >
                                    Uložit
                                </Button>
                            </Stack>
                        ) : (
                            <Stack direction='row' spacing={2}>
                                {hasPermission(UserRole.ADMIN) && (
                                    <Button
                                        variant="outlined"
                                        color="error"
                                        sx={{px: 10}}
                                        onClick={handleDelete}
                                    >
                                        Smazat
                                    </Button>
                                )}
                                <Button
                                    fullWidth
                                    variant="contained"
                                    onClick={() => setEditMode(true)}
                                >
                                    Upravit
                                </Button>
                            </Stack>
                        )
                    )}
                </Stack>
            </Box>
        </Paper>
    );
};

export default CategoryDetails;
